import { IBranch, IContactPerson, IUser } from '../users/models';
import { IDatahubOrderLineDto, IPublicBookingDto } from './dtos';
import { AreaType, PointType } from 'utils';
import { ICaseOrder, ICasePropertyDetails, IDatahubCase } from '../cases/models';
import { IAddress } from '../address/models';
import { IPackage, IProduct } from '../packages/models';
import { IDatahubTask, TaskCategory } from '../tasks/models';
import { ORDER_CREATION_PACKAGE_TYPE } from 'constants/ui/status';
import { IProductDto } from 'api/products/dto';

export enum BookingStatus {
  INCOMPLETE = 'INCOMPLETE',
  RECORDED = 'RECORDED',
  CONFIRMED = 'CONFIRMED',
  FIXED = 'FIXED',
  ON_ROUTE = 'ON_ROUTE',
  ARRIVED = 'ARRIVED',
  COMPLETE = 'COMPLETE',
  CANCELLED = 'CANCELLED',
  UNKNOWN = 'UNKNOWN',
}

export enum OrderStatus {
  Open = 'OPEN',
  Delivered = 'DELIVERED',
  Cancelled = 'CANCELLED',
  Invoiced = 'INVOICED',
  None = 'NONE',
  Backorder = 'BACKORDER',
}

export enum SimplifiedOrderStatus {
  Cancelled = 'CANCELLED',
  Completed = 'COMPLETED',
  Ongoing = 'ONGOING',
  Unknown = 'UNKNOWN',
}

export enum FeedbackType {
  Change = 'CHANGE',
  Complaint = 'COMPLAINT',
  Feedback = 'FEEDBACK',
}

export enum FeedbackDrawingShape {
  Point = 'POINT',
  Arrow = 'ARROW',
  Lasso = 'LASSO',
}

export enum OrderLineType {
  Visual = 'VISUAL',
  Text = 'TEXT',
}

export interface ISimplifiedOrder {
  id: string;
  salesId: string;
  createdDate: Date;
  status: OrderStatus | SimplifiedOrderStatus;
  currency: string;
  totalPrice?: number;
  responsibleUserId: string;
  endUserInvoice?: {
    firstName?: string;
    lastName?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
}

export interface IDrawingWithShape<Shape extends FeedbackDrawingShape> {
  shape: Shape;
  comment?: string;
}

export interface IFeedbackPoint extends IDrawingWithShape<FeedbackDrawingShape.Point> {
  p0: PointType;
  p1: PointType; // left for current implementation support, should to be removed in future
}

export interface IFeedbackArrow extends IDrawingWithShape<FeedbackDrawingShape.Arrow> {
  p0: PointType;
  p1: PointType;
}

export interface IFeedbackLasso extends IDrawingWithShape<FeedbackDrawingShape.Lasso> {
  points: AreaType;
  labelPoint: PointType;
}

export type FeedbackDrawingType = IFeedbackPoint | IFeedbackArrow | IFeedbackLasso;

export interface IFeedbackParams {
  orderlineId: IDatahubOrderLine['id'];
  type: FeedbackType;
  comment: string; // should map to general feedback
  drawingComments: string[];
  uploadedDocuments: string[];

  // copywrite feedback
  salesId?: IDatahubTask['salesId'];
  bookingComment?: string;
  user?: IUser;
}

export interface IElectronicInvoiceParams {
  name: string;
  cvr: string;
  ean?: string;
  phone: string;
  email: string;
  address: IAddress;
  comment?: string;
}

export interface IEndUserInvoiceParams {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  address: IAddress;
}

export interface ICreateOrderParams {
  orderId: ICaseOrder['id'];
  departmentId: IBranch['axCustomerId'];
  branchId: IBranch['id'];
  case:
    | IDatahubCase
    | {
        reference: IDatahubCase['reference'];
        id?: IDatahubCase['id'];
      };
  address?: IAddress | Record<string, never>;
  package?: IPackage;
  products: IProduct[];
  property?: ICasePropertyDetails;
  keyPickupAddress?: IAddress;
  electronicInvoice?: IElectronicInvoiceParams;
  endUserInvoice?: IEndUserInvoiceParams;
  photographerComment: string;
  responsibleUser: Partial<IUser>;
  contactPersons: IContactPerson[];
  watchers: Partial<IUser>[];
  hasExternalStylist: boolean;
  user: IUser;
  anyTaskIncludesBooking: boolean;
}

export interface ICreateOrderResponse {
  salesId: string;
  tasks: IDatahubTask[];
  caseId?: IDatahubCase['id'];
  orderId: ISimplifiedOrder['id'];
}

export interface IUpdateWatchersParams {
  orderId: ISimplifiedOrder['id'];
  userIds: IUser['id'][];
}

export interface IUpdateOrderParams {
  orderId: ISimplifiedOrder['id'];
  caseProducts: Partial<IProduct>[];
}

export interface ISendThirdPartyBooking {
  taskId: IDatahubTask['id'];
  caseId: IDatahubCase['id'];
  realtorName: string;
  recipientPhone: string;
  recipientEmail: string;
  recipientName: string;
}

export interface ICreateOrderChoice {
  itemId: IProduct['itemNumber'];
  config: IProduct['config'];
  quantity: number;
  group: string;
  price?: number;
}

export interface IBookingInfo {
  bookingId: string;
  bookingStartsAt?: Date;
  bookingEndsAt?: Date;
  photographerName: string;
  comments: string;
  status: BookingStatus;
  pickUpAddress?: {
    countryCode?: string;
    street?: string;
    zip?: string;
    city?: string;
    addressFull?: string;
  };
}

export interface IDatahubOrderLine extends Pick<
  IDatahubOrderLineDto,
  'id' | 'productName' | 'batchId' | 'vendorOrderLineId'
> {
  orderedQuantity: IDatahubOrderLineDto['salesQty'];
  deliveredQuantity: IDatahubOrderLineDto['deliveredQty'];
  price: IDatahubOrderLineDto['salesTotal'];
  unit: IDatahubOrderLineDto['salesUnit'];
  status: OrderStatus;
  deliveredAt?: Date;
  canReceiveFeedback: boolean;
  notes?: string;
}

export interface IPublicBooking {
  orderId: IPublicBookingDto['orderId'];
  status: IPublicBookingDto['status'];
}

export interface IPropertyType {
  key: string;
  name: string;
}

//added from v2

export type OrderCreationAddress = Partial<{
  street: string;
  buildingNumber: string;
  buildingFloor: string;
  buildingDoor: string;
  cityArea: string;
  city: string;
  zip: string;
}>;
export type ContactPerson = {
  contactType: string;
  firstName: string;
  lastName?: string;
  phone?: string;
  alternativePhone?: string;
  email?: string;
};
export type BookingInfo = {
  bookingRequested: boolean;
  address: OrderCreationAddress;
  pickUpKeyRequested: boolean;
  pickUpKeyAddress: OrderCreationAddress;
  commentForPhotographers: string;
  contactPersons: ContactPerson[];
  salesStylingRequested: boolean;
};
export type EndUserInvoicing = {
  requested: boolean;
  address: OrderCreationAddress;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};
export type BusinessInvoicing = EndUserInvoicing & {
  name: string;
  cvr: string;
  email: string;
  phone: string;
  ean: string;
  comment: string;
};
export type Invoicing = {
  endUserInvoicing: Partial<EndUserInvoicing>;
  businessInvoicing: Partial<BusinessInvoicing>;
};
export type Product = {
  productId: string;
  quantity: number;
};

export type OrderCreationPayload = Partial<{
  address: IAddress;
  orderId: string;
  caseId: string;
  bookingInfo: BookingInfo;
  propertyInfo: ICasePropertyDetails;
  responsibleEmail: string;
  package: string;
  products: Product[];
  productChoices: ICreateOrderChoice[];
  invoicing: Partial<Invoicing>;
  watcherEmails: string[];
}>;

export type IOrderContact = Omit<IContactPerson, 'type'> & {
  contactType: string;
};
export type CreateNewOrderPayload = Partial<{
  //v2
  address: IAddress;
  orderId: string;
  caseId: string;
  branchId: string;
  reference: string;
  bookingInfo: BookingInfo;
  propertyInfo: ICasePropertyDetails;
  responsibleEmail: string;
  package: string;
  products: Product[];
  productChoices: ICreateOrderChoice[];
  invoicing: Partial<Invoicing>;
  watcherEmails: string[];
  bundleIds: Array<string>;
  addons: Array<{ productId: string; quantity: number }>;
  orderContacts: IOrderContact[];
  type: ORDER_CREATION_PACKAGE_TYPE;
}>;

export type ProductChoice = {
  itemId: string;
  config: string;
  group: string;
  quantity: number;
  id: string;
  tasks: { type: TaskCategory }[];
  price?: number;
  choiceName?: string;
};

export type OrderProductsForm = {
  config?: string; //name
  productId: string;
  quantity: number;
  tasks?: { type: TaskCategory }[];
  price?: number;
  itemId?: string;
  individualProductName?: string;
  maxOrderAmount?: number;
};

export type CaseProperty = {
  type: string;
  livingArea: number;
  basementArea: number;
  floors: number;
};

export type OrderCreationForm = {
  //step1
  address: IAddress;
  property: CaseProperty;
  hasExternalStylist: boolean;
  keyPickup: boolean;
  pickUpKeyAddress: IAddress;
  shouldSendEndUserInvoice: boolean;
  endUserInvoice: Partial<Omit<IEndUserInvoiceParams, 'address'>> & {
    address: IAddress;
    name?: string;
    phone?: string;
    email?: string;
  };
  shouldSendElectronicInvoice: boolean;
  electronicInvoice: Partial<Omit<IElectronicInvoiceParams, 'address'>> & {
    address: IAddress;
    phone?: string;
    email?: string;
  };
  branchId: string;
  case: IDatahubCase | { reference: string; isArchived: boolean; departmentId?: string; id?: string };
  contactPersons: IContactPerson[];
  explicitlySelectedDepartmentId: boolean;
  orderId: ICaseOrder['id'];
  packageType: ORDER_CREATION_PACKAGE_TYPE;
  products?: OrderProductsForm[];
  photographerComment: string;
  isRealtorParticipates?: boolean;
  responsibleEmail: string;
  watcherEmails: string[];
  longitude?: number;
  latitude?: number;
  //step2
  packages?: IPackage;
  status?: 'draft' | 'processing' | 'done';
  isCustomizePackage?: boolean;
  //step3
  productChoices?: ProductChoice[];
};

export type OrderAdsCampaignForm = {
  radius: number;
};

export type CreateNewOrderFormType = {
  //step1
  address: IAddress;
  property: CaseProperty;
  hasExternalStylist: boolean;
  keyPickup: boolean;
  pickUpKeyAddress: IAddress;
  shouldSendEndUserInvoice: boolean;
  endUserInvoice: Partial<Omit<IEndUserInvoiceParams, 'address'>> & {
    address: IAddress;
    name?: string;
    phone?: string;
    email?: string;
  };
  shouldSendElectronicInvoice: boolean;
  electronicInvoice: Partial<Omit<IElectronicInvoiceParams, 'address'>> & {
    address: IAddress;
    phone?: string;
    email?: string;
  };
  branchId: string;
  case: IDatahubCase | { reference: string; isArchived: boolean; departmentId?: string; id?: string };
  contactPersons: IContactPerson[];
  explicitlySelectedDepartmentId: boolean;
  orderId: ICaseOrder['id'];
  packageType: ORDER_CREATION_PACKAGE_TYPE;
  products?: OrderProductsForm[];
  photographerComment: string;
  responsibleEmail: string;
  watcherEmails: string[];
  longitude?: number;
  latitude?: number;
  //step2
  packages?: IPackage;
  basicPackage?: IProductDto;
  webPackages?: IProductDto[];
  somePackages?: IProductDto[];
  textPackages?: IProductDto[];
  dronePackages?: IProductDto[];
  marketingPackages?: IProductDto[];
  imageProducts?: IProductDto[];
  filmProducts?: IProductDto[];
  textProducts?: IProductDto[];
  status?: 'draft' | 'processing' | 'done';
  isCustomizePackage?: boolean;
  //step3
  productChoices?: ProductChoice[];

  bundlePackagesProduct?: Record<string, IProductDto[]>;
  singleProducts?: Record<string, IProductDto[]>;
};
